import { Observable } from 'rxjs';

import { SnLonLat, SnLonLatLike, SnLonLatBounds, SnLonLatBoundsLike } from '@lib/geo/helpers';

import { IFsMap } from '../types';

export abstract class FsMapGeometryServiceProvider<TMap extends IFsMap> {
  abstract readonly geospatialProvider: string;
  abstract readonly id: string;

  // Service composition - delegate to individual services
  abstract readonly bounds: {
    getBounds(): SnLonLatBounds;
    fitBounds(bounds: SnLonLatBoundsLike): void;
    panToBounds(bounds: SnLonLatBoundsLike): void;
  };
  abstract readonly zoom: {
    getZoom(): number;
    setZoom(zoom: number): void;
    zoomIn(): void;
    zoomOut(): void;
    getMaxZoomAtLatLng?(location: SnLonLatLike): Observable<number>;
  };
  abstract readonly navigation: {
    getCenter(): SnLonLat;
    setCenter(coords: SnLonLatLike): void;
    goToCoordinates(coords: SnLonLatLike, zoom?: number): void;
    panTo(coords: SnLonLatLike, options?: Record<string, unknown>): Promise<void>;
  };

  // Map initialization
  abstract setMap(map: TMap): void;

  // Delegate methods to individual services
  public getZoom(): number {
    return this.zoom.getZoom();
  }

  public setZoom(zoom: number): void {
    this.zoom.setZoom(zoom);
  }

  public getCenter(): SnLonLat {
    return this.navigation.getCenter();
  }

  public setCenter(coords: SnLonLatLike): void {
    this.navigation.setCenter(coords);
  }

  public goToCoordinates(coords: SnLonLatLike, zoom?: number): void {
    this.navigation.goToCoordinates(coords, zoom);
  }

  public panTo(coords: SnLonLatLike, options?: Record<string, unknown>): void {
    this.navigation.panTo(coords);
  }

  public panToBounds(bounds: SnLonLatBoundsLike): void {
    this.bounds.panToBounds(bounds);
  }

  public getBounds(): SnLonLatBounds | Record<string, number> {
    return this.bounds.getBounds();
  }

  public fitBounds(bounds: SnLonLatBoundsLike): void {
    this.bounds.fitBounds(bounds);
  }

  public zoomIn(): void {
    this.zoom.zoomIn();
  }

  public zoomOut(): void {
    this.zoom.zoomOut();
  }

  public getMaxZoomAtLatLng?(location: SnLonLatLike): Observable<number> | null {
    return this.zoom.getMaxZoomAtLatLng?.(location) ?? null;
  }
}
